/**
 * Shared CURB/ROI fee calculation logic used by RAP ROI Fee milestone and Add Fees form.
 */

const CURB_HIGH_PERCENT = 49.8;
const CURB_MEDIUM_PERCENT = 33.2;
const CURB_LOW_PERCENT = 24.9;
const CURB_FACTOR_COUNT = 4;
/** Marks the meta element inside the stored implementationCalc array. */
export const CURB_META_KEY = '__curbFeeState';

export interface CurbFeeTypeRow {
  type: string;
  priority?: string;
  high?: boolean;
  medium?: boolean;
  low?: boolean;
}

export interface ProposedFeesInput {
  year1Savings: number | null | undefined;
  year2Savings: number | null | undefined;
  implementationFeeTypes: CurbFeeTypeRow[];
  thirdPartyFee?: number | null | undefined;
}

export interface ProposedFeesResult {
  y1Proposed: number;
  y2Proposed: number;
  avgPercent: number;
  /** Y1 fee before the third-party carve-out. Persisted so later deltas are exact. */
  y1Base: number;
}

/**
 * Computes the average CURB percent from implementation fee type priorities.
 * Only rows with type !== 'Select All' are included; percentSum is divided by 4.
 */
export function getCurbAvgPercent(implementationFeeTypes: CurbFeeTypeRow[]): number {
  let percentSum = 0;
  for (const line of implementationFeeTypes) {
    if (line.type === 'Select All') continue;
    if (line.priority === 'high') percentSum += CURB_HIGH_PERCENT;
    else if (line.priority === 'medium') percentSum += CURB_MEDIUM_PERCENT;
    else if (line.priority === 'low') percentSum += CURB_LOW_PERCENT;
  }
  return percentSum / CURB_FACTOR_COUNT;
}

/**
 * Calculates proposed Y1 and Y2 fees from CURB factors and optional third-party fee.
 * - Y1: base CURB fee (year1Savings * avgPercent / 100), then subtract thirdPartyFee
 *       only if thirdPartyFee is a positive number and the result would be non-negative.
 * - Y2: year2Savings * avgPercent / 100 (no third-party subtraction).
 */
export function calculateProposedFees(input: ProposedFeesInput): ProposedFeesResult {
  const avgPercent = getCurbAvgPercent(input.implementationFeeTypes);
  const year1 = input.year1Savings != null ? Number(input.year1Savings) : 0;
  const year2 = input.year2Savings != null ? Number(input.year2Savings) : 0;

  let y1Proposed = 0;
  let y1Base = 0;
  if (year1) {
    const baseCurbFee = Math.round((year1 * avgPercent) / 100);
    y1Base = baseCurbFee;
    const thirdPartyAmount = input.thirdPartyFee != null ? Number(input.thirdPartyFee) : NaN;
    const hasThirdPartyFee =
      !Number.isNaN(thirdPartyAmount) && thirdPartyAmount > 0 && baseCurbFee - thirdPartyAmount >= 0;
    y1Proposed = hasThirdPartyFee ? Math.round(baseCurbFee - thirdPartyAmount) : baseCurbFee;
  }

  const y2Proposed = year2 ? (year2 * avgPercent) / 100 : 0;

  return { y1Proposed, y2Proposed, avgPercent, y1Base };
}

/**
 * The CURB fee line stores its factors in the cost line's implementationCalc
 * column. Version 1 stored a bare array of factor rows. The current version
 * keeps the array and puts the fee state in a leading meta element.
 *
 * The array must stay an array. The RAP commit endpoint types this field as
 * List<Object>, so an object at the top level does not survive that path and
 * the fee state would be lost on every RAP commit.
 *
 * The override state must be persisted. It cannot be derived by comparing the
 * stored amount against a fresh calculation: year1Savings is not always
 * available on the Fees page, so a fresh calculation can read 0 for a plan that
 * holds a real fee. That comparison marks a system value as user overridden.
 */
export interface ImplementationCalcState {
  factors: CurbFeeTypeRow[];
  userOverride: boolean;
  /** Last system calculated Y1 amount. Used by Reset while an override is active. */
  systemAmount: number | null;
  /**
   * Y1 fee before the third-party carve-out.
   *
   * The base cannot be recovered from the stored amount once the third-party
   * total passed it. The amount then holds the base itself, so adding the old
   * total back gives a number that is too large.
   */
  baseAmount: number | null;
}

/**
 * Reads implementationCalc in either version. A version 1 array reports
 * userOverride false, because version 1 had no override state to store.
 */
export function parseImplementationCalc(raw: any): ImplementationCalcState {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (e) {
      value = null;
    }
  }
  if (Array.isArray(value)) {
    const meta = value.find((row) => row && row[CURB_META_KEY] === true);
    const factors = value.filter((row) => !(row && row[CURB_META_KEY] === true));
    if (meta) {
      return {
        factors: factors,
        userOverride: meta.userOverride === true,
        systemAmount: meta.systemAmount != null ? Number(meta.systemAmount) : null,
        baseAmount: meta.baseAmount != null ? Number(meta.baseAmount) : null
      };
    }
    return { factors: factors, userOverride: false, systemAmount: null, baseAmount: null };
  }
  if (value && Array.isArray(value.factors)) {
    return {
      factors: value.factors,
      userOverride: value.userOverride === true,
      systemAmount: value.systemAmount != null ? Number(value.systemAmount) : null,
      baseAmount: value.baseAmount != null ? Number(value.baseAmount) : null
    };
  }
  return { factors: [], userOverride: false, systemAmount: null, baseAmount: null };
}

/**
 * Builds the stored array: one meta element, then the factor rows.
 *
 * The meta element carries no priority, so getCurbAvgPercent ignores it and the
 * percent is unchanged. parseImplementationCalc strips it, so the factor tables
 * never render it.
 */
export function buildImplementationCalc(state: ImplementationCalcState): any[] {
  const meta: any = {};
  meta[CURB_META_KEY] = true;
  meta.userOverride = state.userOverride === true;
  meta.systemAmount = state.systemAmount != null ? Number(state.systemAmount) : null;
  meta.baseAmount = state.baseAmount != null ? Number(state.baseAmount) : null;
  return [meta].concat(state.factors || []);
}

/** Serializes the stored array for the cost line column. */
export function serializeImplementationCalc(state: ImplementationCalcState): string {
  return JSON.stringify(buildImplementationCalc(state));
}

/** True when the cost line is the CURB fee line. That line carries the factors. */
export function isCurbFeeLine(costLine: any): boolean {
  return !!costLine && costLine.type !== 'Third Party' && costLine.type !== 'Pre Paid'
    && costLine.implementationCalc != null && costLine.implementationCalc !== '';
}

/** Sums every 'Third Party' cost line. A plan can hold more than one. */
export function sumThirdPartyFees(costLines: any[]): number {
  if (!costLines || !costLines.length) return 0;
  return costLines
    .filter((line) => line && line.type === 'Third Party')
    .reduce((total, line) => total + (Number(line.amount) || 0), 0);
}

/**
 * Applies the third-party carve-out to a base CURB fee.
 *
 * The carve-out saturates. When the third-party total is larger than the base
 * fee, the CURB fee stays at the base fee. This matches calculateProposedFees.
 */
export function applyCarveOut(baseAmount: any, thirdPartyTotal: any): number {
  const base = Number(baseAmount) || 0;
  const total = Number(thirdPartyTotal) || 0;
  if (total <= 0 || total > base) return Math.round(base);
  return Math.round(base - total);
}

/**
 * Reads the pre-carve-out base fee for a stored CURB line.
 *
 * Returns null when the base cannot be proven. The caller must then leave the
 * amount alone rather than calculate from a guess.
 *
 * Version 1 lines do not store the base. It can still be recovered while the
 * third-party total is not larger than the stored amount, because saturation
 * needs a total larger than the base, and a saturated line stores the base
 * itself. Above that point a saturated line and a normal line look the same in
 * the stored data, so no rule and no migration can tell them apart. The line
 * gets a real base the next time the fee modal saves it.
 */
export function deriveBaseAmount(state: ImplementationCalcState, storedAmount: any, thirdPartyTotal: any): number | null {
  if (state && state.baseAmount != null) return Number(state.baseAmount) || 0;
  const stored = Number(storedAmount) || 0;
  const total = Number(thirdPartyTotal) || 0;
  if (total <= stored) return stored + total;
  return null;
}
